import React from 'react';
import { Pencil, Router } from 'lucide-react';
import { Button } from '../../ui/Button/Button';
import { CapacityMeter } from '../CapacityMeter';
import { IssueList } from '../IssueList';
import { PanelSection } from '../ui/PanelSection';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { IdentitySheet } from '../sheets/IdentitySheet';
import { sampleRcuConfig } from '../../../data/mockRcuConfig';
import { RCU_HTTP_PORT } from '../../../lib/rcu/catalog/protocol';
import { probeRcu, rcuHost } from '../../../lib/rcu/transport';
import { hasTarget } from '../../../lib/rcu/model';
import { TONE_CHIP } from '../../../lib/rcu/catalog/feedback';
import { issueMessage } from '../../../lib/rcu/catalog/issue';
import type { IssueTarget } from '../../../lib/rcu/catalog/issue';
import { capacityBands, totalOutputCount, wiredOutputCount } from '../../../lib/rcu/query/capacity';
import { validateConfig } from '../../../lib/rcu/validation/validate';
import { useRcuConfigStore } from '../../../stores/rcuConfigStore';
import { useToastStore } from '../../../stores/toastStore';

export interface OverviewPanelProps {
  onSelectTarget: (target: IssueTarget) => void;
}

/** Health of the configuration: identity, capacity and validation in one view. */
export const OverviewPanel: React.FC<OverviewPanelProps> = ({ onSelectTarget }) => {
  const config = useRcuConfigStore((state) => state.config);
  const importConfig = useRcuConfigStore((state) => state.importConfig);
  const success = useToastStore((state) => state.success);
  const error = useToastStore((state) => state.error);
  const [editing, setEditing] = React.useState(false);
  const [loadingSample, setLoadingSample] = React.useState(false);
  const [probing, setProbing] = React.useState(false);

  const { rcu } = config;
  const issues = validateConfig(config);
  const isEmpty = config.rails.length === 0 && config.loads.length === 0;

  const handleTest = (): void => {
    setProbing(true);
    void probeRcu(config)
      .then((delivery) => {
        if (delivery.ok) success(delivery.message);
        else error(delivery.message);
      })
      .finally(() => setProbing(false));
  };

  const handleLoadSample = (): void => {
    const loaded = importConfig(sampleRcuConfig());
    setLoadingSample(false);
    if (loaded.ok) success('Sample layout loaded.');
    else error(issueMessage(loaded.issue));
  };

  return (
    <div className="space-y-4">
      <PanelSection
        title={rcu.name.trim() === '' ? 'Unnamed controller' : rcu.name}
        description={`${rcu.deviceId.trim() === '' ? 'No device ID yet' : rcu.deviceId}${
          rcu.location.trim() === '' ? '' : ` · ${rcu.location}`
        }`}
        action={
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Edit
          </Button>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm ${
              hasTarget(rcu) ? TONE_CHIP.success : TONE_CHIP.warning
            }`}
          >
            <Router className="h-4 w-4" />
            {rcuHost(rcu) ?? 'No address set'}
          </span>
          <span className="text-sm text-[#666666]">
            {rcu.outputCount} onboard outputs · {config.rails.length} rails
          </span>
          <Button variant="secondary" size="sm" onClick={handleTest} disabled={probing}>
            {probing ? 'Contacting…' : 'Find RCU'}
          </Button>
        </div>
        <p className="mt-3 text-xs text-[#666666]">
          Commands are POSTed to <code>http://{'<host>'}:{RCU_HTTP_PORT}/command</code> as the
          firmware&rsquo;s own JSON. The RCU answers every command with{' '}
          <code>{'{"status":"ok"}'}</code>, but a browser cannot read that reply (the firmware sends
          no CORS headers), so a command counts as delivered, not confirmed. The device ID, serial
          number and IP address above are the only things that decide where it goes.
        </p>
        {isEmpty && (
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[#E0E0E0] pt-4">
            <Button variant="secondary" size="sm" onClick={() => setLoadingSample(true)}>
              Load sample layout
            </Button>
            <span className="text-xs text-[#666666]">
              Fills this configuration with example rails, rooms and loads. Replaces what is stored
              now.
            </span>
          </div>
        )}
      </PanelSection>

      <PanelSection title="Capacity" description="Wired outputs against what the hardware provides.">
        <CapacityMeter
          bands={capacityBands(config)}
          totalOutputs={totalOutputCount(config)}
          usedOutputs={wiredOutputCount(config)}
        />
      </PanelSection>

      <PanelSection
        title="Checks"
        description="Errors block deployment. Warnings are usually worth a look."
      >
        <IssueList issues={issues} onSelectTarget={onSelectTarget} />
      </PanelSection>

      {editing && (
        <IdentitySheet isOpen identity={rcu} onClose={() => setEditing(false)} />
      )}

      <ConfirmDialog
        isOpen={loadingSample}
        title="Load sample layout"
        message="This replaces the stored configuration with an example layout: three rails, two rooms and nine loads."
        confirmLabel="Load sample"
        onCancel={() => setLoadingSample(false)}
        onConfirm={handleLoadSample}
      />
    </div>
  );
};

export default OverviewPanel;
